import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { TIERS, mailto, EMAIL } from "@/components/site/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shell = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-[var(--ink)] px-5 py-8 sm:px-10">
    <Link to="/" data-testid="payment-home-link"><Logo /></Link>
    <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center py-16">{children}</div>
  </div>
);

const BackLink = () => (
  <Link to="/" data-testid="payment-back-link" className="mt-10 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[var(--teal)]">
    <ArrowLeft className="h-4 w-4" /> Back to the site
  </Link>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", tier: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return undefined; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        const tier = TIERS.find((t) => t.lookup === data?.lookup_key) || null;
        if (data?.payment_status === "paid") return setState({ status: "paid", tier });
        if (data?.payment_status === "awaiting_transfer") return setState({ status: "transfer", tier, url: data?.instructions_url || null });
        if (["expired", "failed"].includes(data?.payment_status)) return setState({ status: "error", tier });
      } catch { /* keep polling */ }
      tries += 1;
      if (tries < 10) timer = setTimeout(poll, 2000);
      else setState({ status: "error" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (state.status === "checking") return (
    <Shell><p className="flex items-center gap-3 text-lg text-slate-300" data-testid="payment-checking"><Loader2 className="h-5 w-5 animate-spin text-[var(--teal)]" /> Confirming your payment…</p></Shell>
  );
  if (state.status === "transfer") return (
    <Shell>
      <CheckCircle2 className="h-10 w-10 text-[var(--teal)]" />
      <h1 className="font-serif-d mt-6 text-5xl leading-[0.95] text-white sm:text-6xl" data-testid="payment-transfer-title">Order received. <span className="italic text-[var(--teal)]">Now send the transfer.</span></h1>
      <p className="mt-6 text-base leading-relaxed text-slate-300">
        Your resume is saved with your order. We start as soon as your bank transfer arrives, and you'll get a confirmation email when it does.
      </p>
      {state.url && (
        <a href={state.url} target="_blank" rel="noreferrer" data-testid="payment-transfer-instructions"
          className="mt-9 inline-flex w-fit items-center rounded-full bg-[var(--teal)] px-7 py-4 font-semibold text-[var(--ink)] transition-transform duration-300 hover:scale-[1.03]">
          View bank transfer details
        </a>
      )}
      <BackLink />
    </Shell>
  );
  if (state.status === "error") return (
    <Shell>
      <XCircle className="h-10 w-10 text-[var(--amber)]" />
      <h1 className="font-serif-d mt-6 text-5xl text-white" data-testid="payment-error-title">We couldn't confirm that payment.</h1>
      <p className="mt-5 text-slate-300">If you were charged, email us at {EMAIL} and we'll sort it out.</p>
      <BackLink />
    </Shell>
  );
  const name = state.tier?.name || "your order";
  return (
    <Shell>
      <CheckCircle2 className="h-10 w-10 text-[var(--teal)]" />
      <h1 className="font-serif-d mt-6 text-5xl leading-[0.95] text-white sm:text-6xl" data-testid="payment-success-title">Paid. <span className="italic text-[var(--teal)]">We've got your resume.</span></h1>
      <p className="mt-6 text-base leading-relaxed text-slate-300">
        Thanks for choosing {name}. Your resume and target job came through with your order. We'll be in touch by email if we need any real numbers from you.
      </p>
      <a href={mailto(`${name} - question about my order`)} data-testid="payment-email-button"
        className="mt-9 inline-flex w-fit items-center rounded-full border border-white/15 px-7 py-4 font-semibold text-white transition-colors duration-300 hover:border-[var(--teal)] hover:text-[var(--teal)]">
        Questions? Email {EMAIL}
      </a>
      <BackLink />
    </Shell>
  );
};

export const PaymentCancel = () => (
  <Shell>
    <h1 className="font-serif-d text-5xl text-white" data-testid="payment-cancel-title">Checkout cancelled.</h1>
    <p className="mt-5 text-slate-300">No payment was taken. You can place your order again whenever you're ready.</p>
    <BackLink />
  </Shell>
);
